import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AuthorshipSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * MD-13 NUMBER SEQUENCES — "where the counter that obeys §4.9's rules lives".
 *
 * ── WHAT THIS SCREEN IS, AND WHAT IT IS NOT ────────────────────────────────
 *
 * It is the screen that EDITS the record the numbering wave built. The counter
 * itself — one row per tenant per series, incremented inside the caller's own
 * transaction — has existed since 20260818010000, and every hard rule of §4.9
 * is enforced there: a number is handed out once (GEN-83), returned by a
 * rollback (GEN-81), never returned by a delete (GEN-80), and the counter can
 * never fall (BR-MD-13-03, a trigger).
 *
 * What this screen adds is the two columns nobody was reading. `prefix` and
 * `pad_width` were stored from the first day and the printed string still came
 * from thirty-odd TypeScript literals — so a settings screen on top of that
 * would have been a screen whose settings are ignored, which is worse than no
 * screen because it makes a promise the product does not keep. The wave that
 * brings this contract moves every formatter onto the stored columns first;
 * this is the screen that then means something.
 *
 * ── THE THREE STATES OF §7, AND WHY "LOCKED" IS REAL ───────────────────────
 *
 * MD-13 §7 gives the sequence three states, and the third is the interesting
 * one:
 *
 *   Ready      green   the counter works and nothing is missing
 *   Gap found  red     a number was handed out and no document carries it
 *   Locked     grey    "Somebody is confirming a document right now and holds
 *                      the counter." You can wait; you can change nothing.
 *
 * Locked is not decoration. The allocator is `UPDATE … SET next_no = next_no+1
 * … RETURNING`, which takes a ROW LOCK, and a second transaction on the same
 * series blocks there until the first commits — that IS GEN-83's guarantee.
 * So a save on this screen, or even an honest read of it, can queue behind
 * somebody's invoice confirmation. The screen therefore asks for the lock
 * WITHOUT WAITING (`FOR UPDATE NOWAIT` / `SKIP LOCKED`) and reports the answer
 * rather than hanging: a spinner that stops for two seconds and then works is
 * indistinguishable, to the person, from a product that is broken.
 *
 * ── WHY THE GAP CHECK REPORTS AND NEVER REPAIRS ────────────────────────────
 *
 * BR-MD-13-09 says "numbers issued must always equal the next number minus the
 * starting number. Any difference is a gap and must be reported in red." That
 * identity is deliberately NOT a database CHECK, and the numbering migration
 * says why: raising `next_no` by hand on THIS screen is allowed, and it is
 * meant to break the identity. The broken identity is how a deliberate gap
 * becomes VISIBLE instead of silent.
 *
 * So the check reports and never "fixes". Two reasons, both fatal to the
 * repair idea:
 *
 *   a gap can be completely legitimate — a confirmation that rolled back
 *     returns its number (GEN-81), and a hand-raised counter leaves a hole on
 *     purpose (§13's worked example: Meridian moves the invoice counter from
 *     1 to 1000 because they issued 999 invoices on paper);
 *   and a cancelled document KEEPS its number for ever (GEN-80), so
 *     "renumbering to close the gap" would hand a used number to a second
 *     document — the exact defect the counter exists to end.
 *
 * The honest output is a list of numbers with no document behind them and the
 * sentence ERR-MD-13-04 gives: "INV-2017 was given out and no invoice carries
 * it." What happened to it is a question for a person.
 */
// ── The fixed sentences ─────────────────────────────────────────────────────
export const NUMBER_SEQUENCE_MESSAGES = {
    /**
     * ERR-MD-13-01, word for word. The SERVICE does not compose this — the
     * database does, in `number_sequences_forward_only()`, and the screen shows
     * what the database said. That is deliberate: the rule has exactly one
     * enforcement point, and a copy of the sentence in the browser would be a
     * second one that can drift. This entry exists so a test can assert the
     * shape without re-implementing it.
     */
    cannotLower: (lastCode) => `The next number can only go up. ${lastCode} has already been used.`,
    /** ERR-MD-13-02 — names the sequence that holds the prefix. */
    prefixTaken: (prefix, holder) => `${prefix} is already used by ${holder}.`,
    /** ERR-MD-13-03. */
    badWidth: 'The width must be between 1 and 10.',
    /** MD-13 §5 field 3's range, and §15's "Zero"/"Negative"/"Very large". */
    badNextNumber: 'The next number must be a whole number between 1 and 999,999,999.',
    /** ERR-MD-13-04 — one line per number with nothing behind it. */
    gapFound: (code, what) => `${code} was given out and no ${what} carries it.`,
    /** §7's grey state, said as a person would say it. */
    locked: 'Somebody is confirming a document right now and holds the counter. Wait a moment and try again.',
    /** §6's Save confirmation — "a strong confirmation naming what will change". */
    saveConsequence: (nextCode, what) => `The next ${what} will be ${nextCode}. This cannot be undone.`,
    /** MD-13 §15's "Very large", surfaced on the screen rather than at posting. */
    full: (what) => `The ${what} sequence is full. Change its prefix and start again.`,
    /** BR-MD-13-07, said before the person saves rather than after. */
    prefixKeepsHistory: 'Documents already numbered keep the prefix they were issued with. Only the next one changes.',
};
/**
 * D46 — MD-13's two parameterised sequence refusals, structured. `prefixTaken`
 * carries the prefix and the series that already holds it; `full` the series
 * label (lowercased at the raise site). No token, no plural. Sentence parity
 * pinned by `refusal-census.spec.ts`.
 */
export const NUMBER_SEQUENCE_REFUSALS = {
    prefixTaken: (prefix, holder) => ({
        message: NUMBER_SEQUENCE_MESSAGES.prefixTaken(prefix, holder),
        key: 'numseq.errPrefixTaken',
        params: { prefix, holder },
    }),
    full: (what) => ({
        message: NUMBER_SEQUENCE_MESSAGES.full(what),
        key: 'numseq.errFull',
        params: { what },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
/**
 * MD-13 §5 field 2. "Maximum 8 characters, capital letters and one trailing
 * hyphen."
 *
 * The trailing hyphen is NOT required, and that is not laxity — `v` (a BOM
 * version) and a tenant who wants `INV` with no separator are both legal
 * shapes the counter already stores, and a regex demanding a hyphen would
 * refuse rows the product itself created. What IS enforced is the character
 * set: capitals, digits and hyphens, so a prefix can never contain a space or
 * a slash and turn a document code into something a filename or a URL has to
 * escape.
 */
export const PrefixSchema = z
    .string()
    .trim()
    .min(1)
    .max(8)
    .regex(/^[A-Za-z0-9-]+$/, 'A prefix may hold letters, digits and hyphens only.');
/** MD-13 §5 field 4 — 1 to 10 (ERR-MD-13-03). */
export const PadWidthSchema = z.number().int().min(1).max(10);
/** MD-13 §5 field 3 — 1 to 999,999,999 (§15's Zero, Negative and Very large). */
export const NextNumberSchema = z.number().int().min(1).max(999_999_999);
export const NumberSequenceStatusSchema = z.enum(['ready', 'gap_found', 'locked']);
export const NumberSequenceSchema = z
    .object({
    /** The catalogue key — `invoice`, `partner`, … Stable, and the path param. */
    series: z.string(),
    /**
     * The sub-series. Empty for the ordinary one-counter-per-tenant case; a
     * year for the two GEN-77a types, a direction for the two cheque
     * registers, a finished item for a BOM version.
     */
    scope: z.string(),
    /** MD-13 §5 field 1 — "Sales invoice", not `invoice`. */
    document: z.string(),
    prefix: PrefixSchema,
    padWidth: PadWidthSchema,
    /** BR-MD-13-09's right-hand side. Fixed once the sequence has been used. */
    startNo: z.number().int(),
    /** MD-13 §5 field 3. */
    nextNo: z.number().int(),
    /** MD-13 §5 field 6. */
    issuedCount: z.number().int(),
    /** MD-13 §5 field 5 — the LAST code handed out; null before the first. */
    lastGiven: z.string().nullable(),
    /** What the NEXT document will print, so the screen can show the promise. */
    nextCode: z.string(),
    /**
     * §7. `locked` means the row is held by a confirmation happening right
     * now; `gap_found` means issued ≠ next − start, which is BR-MD-13-09's
     * test and NOT proof of a defect — see the header.
     */
    status: NumberSequenceStatusSchema,
    /**
     * next − start − issued. Zero on a clean sequence. Positive means numbers
     * were skipped (a counter raised by hand, §13's own example); the number
     * itself is shown rather than a bare red chip, because "12 numbers were
     * skipped" is answerable and "something is wrong" is not.
     */
    discrepancy: z.number().int(),
    /** MD-13 §15's "Very large" — the sequence has no numbers left to give. */
    isFull: z.boolean(),
    /** MD-13 §5 field 7. Read-only in this build — see the note on the route. */
    restartEachYear: z.boolean(),
    /**
     * MD-RULE-5's six fields — and TWO OF THEM DO NOT MEAN HERE WHAT THEY MEAN rule-coverage:ignore
     * ELSEWHERE, which the screen has to respect:
     *
     *   `createdBy`/`createdByName` are ALWAYS null. These rows are minted at
     *   signup by `app_ensure_number_sequences()`, so no person created them.
     *   The right rendering is "—" or "System", never a blank that reads like
     *   a name that failed to load.
     *
     *   `updatedAt` is NOT "when this record was last changed". It is stamped
     *   by the `number_sequences_forward_only()` trigger, which also fires on
     *   the counter bump inside `app_next_number()` — so it moves every time
     *   any document is numbered, and it is really "when a number was last
     *   issued". `updatedBy`, by contrast, is written ONLY by the MD-13 save,
     *   so it does mean "who last changed this". A screen pairing the two as
     *   "changed by X at T" would be wrong on this one table; show the author
     *   against the save, not against this timestamp.
     */
    ...AuthorshipSchema.shape,
})
    .strict();
export const NumberSequenceListSchema = z
    .object({
    sequences: NumberSequenceSchema.array(),
    /** `admin.number_sequences` or the Owner flag — MD-13 §6's Edit column. */
    canEdit: z.boolean(),
})
    .strict();
/** One number the counter says it issued that no document carries. */
export const NumberGapSchema = z
    .object({
    no: z.number().int(),
    code: z.string(),
    /** ERR-MD-13-04, composed. */
    message: z.string(),
})
    .strict();
export const GapCheckResultSchema = z
    .object({
    series: z.string(),
    scope: z.string(),
    document: z.string(),
    /** start..next-1 — the window the counter claims to have filled. */
    checkedFrom: z.number().int(),
    checkedTo: z.number().int(),
    /**
     * Every number in that window with no row behind it, capped. A sequence
     * that was jumped from 1 to 1,000,000 has a million "gaps" and listing
     * them would help nobody, so the count is exact and the list is a sample.
     */
    gaps: NumberGapSchema.array(),
    gapCount: z.number().int(),
    truncated: z.boolean(),
    /**
     * The one thing the screen must say and the check must not do. A gap can
     * be perfectly legitimate — a rolled-back confirmation returns its number
     * and a cancelled document keeps its own for ever (GEN-80/-81) — so this
     * is a report, never a repair.
     */
    note: z.string(),
})
    .strict();
/**
 * MD-13 §5 field 1's labels — "Sales invoice", not `invoice`.
 *
 * Here rather than in the database because the database has no business
 * knowing what a `delivery_note` is called in English: ERR-MD-13-02's sentence
 * ("DN- is already used by Delivery note") is a product sentence, and the
 * trigger that makes the rule race-proof names the series KEY and lets this
 * turn it into words. It is also the list the i18n pass will translate.
 *
 * Every series the screen lists must appear here. `number-sequences.e2e`
 * asserts it against a real list rather than against the key set, because the
 * failure a missing label produces is small and ugly and lives on the screen:
 * a person shown the word `consumption_voucher` on a settings page.
 */
export const SERIES_LABELS = {
    quotation: 'Quotation',
    sales_order: 'Sales order',
    invoice: 'Sales invoice',
    delivery_note: 'Delivery note',
    credit_note: 'Credit note',
    purchase_order: 'Purchase order',
    goods_receipt: 'Goods receipt',
    purchase_bill: 'Purchase bill',
    purchase_return: 'Purchase return',
    receipt: 'Receipt voucher',
    payment_voucher: 'Payment voucher',
    journal_entry: 'Journal entry',
    stock_transfer: 'Stock transfer',
    cash_transfer: 'Cash transfer',
    work_order: 'Work order',
    bom_version: 'Recipe version',
    partner: 'Business partner',
    item: 'Item',
    unit: 'Unit of measure',
    unit_group: 'Unit group',
    warehouse: 'Warehouse',
    price_tier: 'Price tier',
    check: 'Cheque',
    stock_count: 'Stock count',
    import_order: 'Import file',
    opportunity: 'Opportunity',
    lead: 'Lead',
    fixed_asset: 'Fixed asset',
    employee: 'Employee',
    requisition: 'Job requisition',
    payroll_run: 'Payroll run',
    project: 'Project',
    revaluation: 'FX revaluation',
    depreciation_run: 'Depreciation run',
    pos_sale: 'Point of sale',
    consumption_voucher: 'Material consumption',
};
/** The label, or the key itself if a series is added and the label forgotten. */
export function seriesLabel(series, scope = '') {
    const base = SERIES_LABELS[series] ?? series;
    if (scope === '')
        return base;
    // A sub-series says which one it is, because a screen listing two cheque
    // registers with the same name is a screen you cannot use.
    if (series === 'check')
        return `${base} (${scope})`;
    return `${base} ${scope}`;
}
// ── Routes ──────────────────────────────────────────────────────────────────
const seriesParams = z.object({ series: z.string().min(1).max(40) }).strict();
export const numberSequencesContract = c.router({
    list: {
        method: 'GET',
        path: '/api/settings/number-sequences',
        responses: {
            200: NumberSequenceListSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'MD-13 §4 — one row per series, with prefix, width, next, last given and issued (admin.view; editing needs admin.number_sequences).',
    },
    update: {
        method: 'PATCH',
        path: '/api/settings/number-sequences/:series',
        pathParams: seriesParams,
        query: z.object({ scope: z.string().max(64).optional() }).strict(),
        /**
         * Three fields, and no more. MD-13 §6: "Edit unlocks the prefix, the
         * next number and the width for that row." Everything else on the row
         * is either fixed by the system (the document, the starting number) or
         * derived from what the counter did (issued, last given).
         *
         * `restartEachYear` (field 7) is deliberately absent. GEN-82 makes
         * "unticked" the built default and BR-MD-13-08 forbids restarting
         * unless it is ticked — so the shipped behaviour is already correct with
         * the switch permanently off. Shipping a WRITEABLE switch would mean
         * shipping the restart machinery it implies (a year scope appearing on
         * every series mid-life, and every existing number re-read against it),
         * and a switch that saves but changes nothing is the same broken promise
         * this whole wave exists to end. It is reported as read-only false.
         */
        body: z
            .object({
            prefix: PrefixSchema.optional(),
            padWidth: PadWidthSchema.optional(),
            nextNo: NextNumberSchema.optional(),
        })
            .strict(),
        responses: {
            200: NumberSequenceSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            /**
             * Three different refusals share this code and each names itself:
             * a lowered next number (ERR-MD-13-01, the DATABASE's own sentence),
             * a prefix another sequence holds (ERR-MD-13-02), and the row being
             * locked by a confirmation happening right now (§7's grey state).
             */
            409: MessageSchema,
        },
        summary: 'MD-13 §6 Save — prefix, width and next number (admin.number_sequences). The next number may only be raised (ERR-MD-13-01).',
    },
    checkGaps: {
        method: 'GET',
        path: '/api/settings/number-sequences/:series/gaps',
        pathParams: seriesParams,
        query: z.object({ scope: z.string().max(64).optional() }).strict(),
        responses: {
            200: GapCheckResultSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'MD-13 §6 Check for gaps — every number the counter issued that no document carries (admin.view). Reports; never repairs.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=number-sequences.js.map